import { useState, useEffect, useRef, type FormEvent } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion'; // PWA template uses framer-motion instead of motion/react
import { Calendar, Clock, MapPin, ChevronRight, Filter, Bird } from 'lucide-react';
import toast from 'react-hot-toast';
import Logo from '../components/Logo';
import { outingsService } from '../../../services/jacanaDbService';
import { Outing, isOutingPublished } from '../types';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import PrivacyLinksBlock from '../../legal/components/PrivacyLinksBlock';
import { setMarketingConsent } from '../../legal/consentService';
import { HOME_CLIENT_SETTINGS } from '../config/homeSettings';
import { getOutingPricingDisplay } from '../pricing/pricingDisplay';
import useAppUser from '../../../hooks/useAppUser';
import { useLegalConsentSummary } from '../../legal/useLegalConsentSummary';
import { queryClient } from '../../../queryClient';

import { useScroll, useTransform } from 'framer-motion';

const DEFAULT_LATE_BOOKING_HOURS_BEFORE = 4;
const NEWSLETTER_EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function isValidNewsletterEmail(email: string) {
  return NEWSLETTER_EMAIL_PATTERN.test(email);
}

function resolveNewsletterErrorMessage(error: unknown) {
  const normalizedError = error as { code?: string; message?: string } | null;
  const message = String(normalizedError?.message || '');
  const code = String(normalizedError?.code || '');

  if (message.includes('EMAIL_MISMATCH')) {
    return 'Veuillez utiliser l adresse email associee a votre compte.';
  }

  if (message.includes('INVALID_EMAIL')) {
    return 'Veuillez renseigner une adresse email valide.';
  }

  if (message.includes('NEWSLETTER_REQUIRES_ACTIVE_CONSENT')) {
    return 'Cette inscription newsletter doit confirmer un consentement actif.';
  }

  if (message.includes('INVALID_MARKETING_SOURCE')) {
    return 'Le formulaire newsletter n a pas pu etre traite correctement.';
  }

  if (message.includes('NEWSLETTER_RATE_LIMITED') || code.includes('resource-exhausted')) {
    return 'Trop de tentatives recentes. Merci de patienter avant de reessayer.';
  }

  if (code.includes('failed-precondition')) {
    return 'Cette demande ne peut pas etre traitee pour le moment.';
  }

  if (code.includes('unavailable') || code.includes('deadline-exceeded') || code.includes('network')) {
    return 'Impossible de joindre le serveur pour le moment. Verifiez votre connexion et reessayez.';
  }

  return 'Le serveur n a pas pu enregistrer votre inscription newsletter pour le moment.';
}

function padDatePart(value: number) {
  return String(value).padStart(2, '0');
}

function formatOutingReferenceDate(date: Date) {
  return `${date.getFullYear()}-${padDatePart(date.getMonth() + 1)}-${padDatePart(date.getDate())}T${padDatePart(date.getHours())}:${padDatePart(date.getMinutes())}:${padDatePart(date.getSeconds())}`;
}

function toOutingTimeMs(value: string) {
  const parsed = new Date(value).getTime();
  return Number.isNaN(parsed) ? null : parsed;
}

function getBookingStatus(outing: Outing): { closed: boolean; active: boolean; statusLabel: string; detailLabel: string | null } {
  const now = new Date();
  const outingDate = new Date(outing.date);
  const lateBookingsEnabled = outing.allowLateBookings === true;
  const lateBookingHoursBefore = outing.lateBookingHoursBefore;
  const hasValidLateBookingHours =
    typeof lateBookingHoursBefore === 'number'
    && lateBookingHoursBefore > 0
    && lateBookingHoursBefore < 24;
  const hoursBeforeClose: number = lateBookingsEnabled
    ? (hasValidLateBookingHours ? lateBookingHoursBefore : DEFAULT_LATE_BOOKING_HOURS_BEFORE)
    : 24;
  const effectiveCloseTime = new Date(outingDate.getTime() - hoursBeforeClose * 60 * 60 * 1000);
  const isLateBookingActive = lateBookingsEnabled && now >= new Date(outingDate.getTime() - 24 * 60 * 60 * 1000) && now < effectiveCloseTime;
  const isClosed = now >= effectiveCloseTime;

  return {
    closed: isClosed,
    active: isLateBookingActive,
    statusLabel: isLateBookingActive ? 'Inscriptions de dernière minute ouvertes' : (isClosed ? 'Réservations clôturées' : 'Réservations ouvertes'),
    detailLabel: lateBookingsEnabled || isClosed
      ? `Jusqu'au ${format(effectiveCloseTime, 'dd MMM HH:mm', { locale: fr })}`
      : null,
  };
}

export default function Home() {
  const pageLoadedAtRef = useRef(new Date());
  const pageLoadedAtMs = pageLoadedAtRef.current.getTime();
  const pageLoadedAtComparable = formatOutingReferenceDate(pageLoadedAtRef.current);
  const { firebaseUser, loading: authLoading } = useAppUser();
  const { data: legalConsentSummary, isLoading: legalConsentSummaryLoading } = useLegalConsentSummary();
  const [outings, setOutings] = useState<Outing[]>([]);
  const [loading, setLoading] = useState(true);
  const [pastOutings, setPastOutings] = useState<Outing[]>([]);
  const [pastOutingsLoading, setPastOutingsLoading] = useState(false);
  const [pastOutingsLoaded, setPastOutingsLoaded] = useState(false);
  const [pastOutingsError, setPastOutingsError] = useState<string | null>(null);
  const [newsletterEmail, setNewsletterEmail] = useState('');
  const [newsletterConsentChecked, setNewsletterConsentChecked] = useState(false);
  const [newsletterSubmitting, setNewsletterSubmitting] = useState(false);
  const [newsletterMessage, setNewsletterMessage] = useState<string | null>(null);
  const location = useLocation();
  const { scrollY } = useScroll();
  const y1 = useTransform(scrollY, [0, 800], [0, 200]);
  const y2 = useTransform(scrollY, [0, 800], [0, -150]);
  const isAuthenticated = Boolean(firebaseUser);
  const newsletterAccountEmail = firebaseUser?.email?.trim().toLowerCase() || '';
  const isAuthenticatedNewsletterSubscribed = isAuthenticated && legalConsentSummary?.marketingEmailAccepted === true;
  const shouldHideNewsletterSignup = !HOME_CLIENT_SETTINGS.showNewsletterSignup
    || authLoading
    || (isAuthenticated && (legalConsentSummaryLoading || !newsletterAccountEmail || isAuthenticatedNewsletterSubscribed));

  useEffect(() => {
    async function load() {
      try {
        const data = await outingsService.getAll();

        const upcomingOutings = data.filter(outing => {
          const outingTimeMs = toOutingTimeMs(outing.date);
          return outingTimeMs !== null
            && outingTimeMs >= pageLoadedAtMs
            && !outing.deleted
            && isOutingPublished(outing);
        });

        setOutings(upcomingOutings);
      } catch (error) {
        console.error("Failed to load outings", error);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [pageLoadedAtMs]);

  useEffect(() => {
    if (loading) return;
    if (location.hash !== '#sorties') return;

    const scrollToCatalogue = () => {
      document.getElementById('sorties')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    window.requestAnimationFrame(() => {
      window.setTimeout(scrollToCatalogue, 50);
    });
  }, [location.hash, loading]);

  const handleNewsletterSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (newsletterSubmitting) {
      return;
    }

    const normalizedEmail = isAuthenticated
      ? newsletterAccountEmail
      : newsletterEmail.trim().toLowerCase();

    if (!normalizedEmail) {
      setNewsletterMessage(
        isAuthenticated
          ? 'Aucune adresse email de compte n est disponible pour cette inscription.'
          : 'Veuillez renseigner votre adresse email.'
      );
      return;
    }

    if (!isValidNewsletterEmail(normalizedEmail)) {
      setNewsletterMessage('Veuillez renseigner une adresse email valide.');
      return;
    }

    if (!newsletterConsentChecked) {
      setNewsletterMessage('Vous devez confirmer explicitement votre accord avant l inscription.');
      return;
    }

    setNewsletterSubmitting(true);
    setNewsletterMessage(null);

    try {
      const response = await setMarketingConsent({
        email: normalizedEmail,
        granted: true,
        source: 'newsletter',
        locale: typeof navigator !== 'undefined' ? navigator.language : null,
        metadata: {
          placement: 'home_newsletter',
        },
      });

      setNewsletterEmail('');
      setNewsletterConsentChecked(false);
      if (response.status === 'already_subscribed') {
        setNewsletterMessage('Cette adresse est déjà inscrite à la newsletter.');
        toast.success('Adresse déjà inscrite à la newsletter.');
        if (isAuthenticated && firebaseUser?.uid) {
          await queryClient.invalidateQueries({ queryKey: ['legal-consent-summary', firebaseUser.uid] });
        }
        return;
      }

      setNewsletterMessage('Votre inscription à la newsletter a bien été enregistrée.');
      toast.success('Inscription newsletter enregistrée.');
      if (isAuthenticated && firebaseUser?.uid) {
        await queryClient.invalidateQueries({ queryKey: ['legal-consent-summary', firebaseUser.uid] });
      }
    } catch (error) {
      console.error('Erreur lors de l inscription newsletter:', error);
      setNewsletterMessage(resolveNewsletterErrorMessage(error));
      toast.error('Échec de l inscription newsletter.');
    } finally {
      setNewsletterSubmitting(false);
    }
  };

  const handleLoadPastOutings = async () => {
    if (pastOutingsLoaded) return; // Already loaded, prevent re-fetch
    
    setPastOutingsLoading(true);
    setPastOutingsError(null);
    
    try {
      const data = await outingsService.getPastOutings(8, pageLoadedAtComparable);
      setPastOutings(data);
      setPastOutingsLoaded(true);
    } catch (error) {
      console.error("Failed to load past outings", error);
      setPastOutingsError("Impossible de charger les expéditions passées pour le moment.");
    } finally {
      setPastOutingsLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-accent"></div>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.8 }}
      className="space-y-32 pt-12 md:pt-32 pb-32 max-w-7xl mx-auto px-4 md:px-6"
    >
      {/* Hero Section */}
      <section className="relative h-[85vh] min-h-[500px] md:min-h-[600px] rounded-[40px] md:rounded-[80px] overflow-hidden group">
        <motion.img
          style={{ y: y1 }}
          src="https://images.unsplash.com/photo-1441974231531-c6227db76b6e?auto=format&fit=crop&q=80&w=2560"
          alt="Nature"
          className="absolute inset-0 w-full h-[120%] object-cover scale-110 group-hover:scale-105 transition-transform duration-[5s] ease-out"
          referrerPolicy="no-referrer"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-brand-primary/95 via-brand-primary/40 to-transparent flex flex-col justify-end p-6 md:p-24">
          <motion.div
            style={{ y: y2 }}
            className="max-w-4xl space-y-6 md:space-y-10"
          >
            <div className="inline-flex items-center gap-4 bg-white/10 backdrop-blur-xl border border-white/20 px-4 md:px-6 py-2 md:py-3 rounded-full text-[9px] md:text-[10px] font-bold uppercase tracking-[0.2em] md:tracking-[0.3em] text-white font-sans">
              <Logo iconOnly className="w-4 h-4 md:w-5 md:h-5 invert" />
              Expériences en nature
            </div>
            <h1 className="text-4xl sm:text-5xl md:text-[110px] font-serif text-white font-bold leading-[1.1] md:leading-[0.9] tracking-tighter italic">
              L'appel de <br className="hidden sm:block" />la nature.
            </h1>
            <p className="text-white/80 text-lg md:text-2xl font-serif italic max-w-2xl leading-relaxed">
              "Chaque pas est une rencontre. Chaque observation est une découverte. Bienvenue dans une expérience qui ravive vos liens avec la nature."
            </p>
            <div className="pt-6 md:pt-12 flex flex-col sm:flex-row gap-4 md:gap-6 font-sans">
              <button
                onClick={() => document.getElementById('sorties')?.scrollIntoView({ behavior: 'smooth' })}
                className="bg-white text-brand-primary px-8 md:px-12 py-4 md:py-6 rounded-full text-[9px] md:text-[10px] font-bold uppercase tracking-[0.2em] md:tracking-[0.3em] hover:bg-brand-paper transition-all duration-500 shadow-2xl w-full sm:w-auto"
              >
                Explorer les expéditions
              </button>
              <button className="bg-white/10 backdrop-blur-xl border border-white/20 text-white px-8 md:px-12 py-4 md:py-6 rounded-full text-[9px] md:text-[10px] font-bold uppercase tracking-[0.2em] md:tracking-[0.3em] hover:bg-white/20 transition-all duration-500 w-full sm:w-auto">
                Notre philosophie
              </button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Philosophy Section */}
      <section className="grid grid-cols-1 lg:grid-cols-2 gap-32 items-center px-6">
        <div className="space-y-12">
          <div className="space-y-6">
            <h4 className="text-[10px] font-bold uppercase tracking-[0.4em] text-brand-accent font-sans">Philosophie</h4>
            <h2 className="text-5xl md:text-7xl font-serif font-bold italic leading-[1.1] text-brand-primary">
              Une autre façon <br />de vivre la nature.
            </h2>
          </div>
          <div className="space-y-8 text-xl font-serif italic leading-relaxed text-brand-primary/60">
            <p>
              Je suis convaincue que la nature ne se traverse pas, elle se découvre.
            </p>
            <p>
              À travers Jacana, je vous invite à ralentir, à observer, à écouter et à ressentir ce qui nous entoure. Derrière le chant d'un oiseau, les traces d'un animal ou la forme d'une feuille se cachent des histoires fascinantes que l'on oublie souvent de regarder.
            </p>
            <p>
              Mon objectif n'est pas seulement de vous faire découvrir un lieu, mais de vous transmettre un autre regard sur la nature. Un regard plus curieux, plus attentif et plus respectueux du vivant.
            </p>
            <p>
              J'aime prendre le temps d'observer, de partager mes connaissances et de m'émerveiller avec vous. Car je suis persuadée que l'on protège mieux ce que l'on comprend… et que l'on comprend mieux ce que l'on prend le temps d'observer.
            </p>
            <p>
              Bienvenue dans l'univers de Jacana, rejoignez-moi pour vivre la nature autrement.
            </p>
          </div>
          <div className="pt-8 flex items-center gap-8">
            <div className="w-20 h-20 rounded-full bg-brand-accent/5 flex items-center justify-center border border-brand-accent/10">
              <Bird size={32} className="text-brand-accent" />
            </div>
            <div className="space-y-1 font-sans">
              <span className="text-[10px] font-bold uppercase tracking-[0.3em] block text-brand-primary">Observation & Conscience</span>
              <span className="text-sm font-serif italic opacity-40 text-brand-primary font-serif">Immersion naturaliste en Ardenne</span>
            </div>
          </div>
        </div>
        <div className="relative">
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            whileInView={{ scale: 1, opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 1 }}
            className="aspect-[3/4] rounded-[80px] overflow-hidden border border-brand-primary/5 shadow-2xl"
          >
            <img
              src="https://images.unsplash.com/photo-1511497584788-876760111969?auto=format&fit=crop&q=80&w=1000"
              alt="Forest"
              className="w-full h-full object-cover"
              referrerPolicy="no-referrer"
            />
          </motion.div>
          <div className="absolute -bottom-12 -left-12 bg-white p-12 rounded-[48px] shadow-2xl border border-brand-primary/5 hidden md:block">
            <div className="text-[10px] font-bold uppercase tracking-[0.2em] opacity-40 text-brand-primary font-sans">Depuis</div>
            <div className="text-5xl font-serif font-bold italic mb-2 text-brand-primary">2024</div>
          </div>
        </div>
      </section>

      {/* Catalogue Section */}
      <section id="sorties" className="space-y-16 px-6 scroll-mt-32">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-8 border-b border-brand-primary/5 pb-12">
          <div className="space-y-4">
            <h4 className="text-[10px] font-bold uppercase tracking-[0.4em] text-brand-accent font-sans">Catalogue</h4>
            <h2 className="text-5xl md:text-6xl font-serif font-bold italic text-brand-primary">Prochaines Expéditions</h2>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-16">
          {outings.length === 0 ? (
            <div className="col-span-full text-center py-32 bg-white rounded-[48px] border border-dashed border-brand-primary/10">
              <p className="text-brand-primary/40 font-serif italic text-2xl">Aucune expédition prévue pour le moment.</p>
            </div>
          ) : (
            outings.map((outing, index) => (
              (() => {
                const pricingDisplay = getOutingPricingDisplay(outing);
                const bookingStatus = getBookingStatus(outing);
                return (
              <motion.div
                key={outing.id}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{ delay: index * 0.15, duration: 1, ease: [0.22, 1, 0.36, 1] }}
                className="group"
              >
                <Link to={`/outing/${outing.id}`}>
                  <div className="relative aspect-[4/5] overflow-hidden rounded-[56px] mb-8 border border-brand-primary/5 shadow-lg group-hover:shadow-2xl transition-all duration-700">
                    <img
                      src={outing.imageUrl || `https://images.unsplash.com/photo-1441974231531-c6227db76b6e?auto=format&fit=crop&q=80&w=600`}
                      alt={outing.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-[2s] ease-out"
                      referrerPolicy="no-referrer"
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.src = `https://picsum.photos/seed/${outing.id}/600/800`;
                      }}
                    />
                    <div className="absolute top-8 left-8 bg-brand-paper/90 backdrop-blur-md px-5 py-2 rounded-full text-[10px] font-bold uppercase tracking-[0.2em] border border-white/20 shadow-xl text-brand-primary font-sans">
                      {outing.difficulty || 'Facile'}
                    </div>
                    <div className="absolute bottom-8 left-8 right-8 bg-white/10 backdrop-blur-xl border border-white/20 p-6 rounded-[32px] opacity-0 group-hover:opacity-100 translate-y-4 group-hover:translate-y-0 transition-all duration-500">
                      <div className="flex items-center justify-between text-white">
                        <span className="text-2xl font-serif italic">{pricingDisplay.homePrimaryLabel || 'Tarifs disponibles'}</span>
                        <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest font-sans">
                          Détails <ChevronRight size={14} />
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="space-y-4 px-2">
                    <div className="flex items-center gap-4 text-[10px] font-bold uppercase tracking-[0.3em] opacity-30 text-brand-primary font-sans">
                      <span>{format(new Date(outing.date), 'dd MMMM', { locale: fr })}</span>
                      <span>•</span>
                      <span>{outing.duration}</span>
                      <span>•</span>
                      <span className="flex items-center gap-1"><MapPin size={10} /> {outing.location}</span>
                    </div>
                    <h3 className="text-3xl font-serif font-bold leading-tight group-hover:text-brand-accent transition-colors duration-500 text-brand-primary">
                      {outing.title}
                    </h3>
                    <p className="text-[11px] md:text-[12px] font-semibold text-brand-accent uppercase tracking-[0.1em] mt-2">
                      {bookingStatus.statusLabel}
                    </p>
                    {bookingStatus.detailLabel ? (
                      <p className="text-[12px] md:text-[13px] text-brand-primary/60">
                        {bookingStatus.detailLabel}
                      </p>
                    ) : null}
                    {pricingDisplay.homePrimaryLabel ? (
                      <p className="text-[12px] md:text-[13px] font-semibold text-brand-primary/75">
                        {pricingDisplay.homePrimaryLabel}
                      </p>
                    ) : null}
                    {pricingDisplay.homeBadges.length > 0 ? (
                      <div className="flex flex-wrap gap-2 pt-1">
                        {pricingDisplay.homeBadges.map((badge) => (
                          <span key={badge} className="rounded-full border border-brand-primary/10 bg-brand-paper px-4 py-2 text-[10px] font-bold uppercase tracking-[0.12em] text-brand-primary/70 font-sans">
                            {badge}
                          </span>
                        ))}
                      </div>
                    ) : null}
                  </div>
                </Link>
              </motion.div>
                );
              })()
            ))
          )}
        </div>
      </section>

      {/* Past Outings Section - Lazy Loaded */}
      {pastOutingsLoaded && pastOutings.length > 0 && (
        <section className="space-y-16 px-6 scroll-mt-32">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-8 border-b border-brand-primary/5 pb-12">
            <div className="space-y-4">
              <h4 className="text-[10px] font-bold uppercase tracking-[0.4em] text-brand-accent font-sans">Archives</h4>
              <h2 className="text-5xl md:text-6xl font-serif font-bold italic text-brand-primary">Expéditions Passées</h2>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-16">
            {pastOutings.map((outing, index) => (
              (() => {
                const pricingDisplay = getOutingPricingDisplay(outing);
                const bookingStatus = getBookingStatus(outing);
                return (
              <motion.div
                key={outing.id}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{ delay: index * 0.15, duration: 1, ease: [0.22, 1, 0.36, 1] }}
                className="group opacity-75 hover:opacity-100 transition-opacity duration-300"
              >
                <div className="relative aspect-[4/5] overflow-hidden rounded-[56px] mb-8 border border-brand-primary/5 shadow-lg group-hover:shadow-2xl transition-all duration-700">
                  <img
                    src={outing.imageUrl || `https://images.unsplash.com/photo-1441974231531-c6227db76b6e?auto=format&fit=crop&q=80&w=600`}
                    alt={outing.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-[2s] ease-out"
                    referrerPolicy="no-referrer"
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.src = `https://picsum.photos/seed/${outing.id}/600/800`;
                    }}
                  />
                  <div className="absolute top-8 left-8 bg-brand-paper/90 backdrop-blur-md px-5 py-2 rounded-full text-[10px] font-bold uppercase tracking-[0.2em] border border-white/20 shadow-xl text-brand-primary font-sans">
                    {outing.difficulty || 'Facile'}
                  </div>
                  <div className="absolute top-8 right-8 bg-brand-accent/90 backdrop-blur-md px-5 py-2 rounded-full text-[10px] font-bold uppercase tracking-[0.2em] border border-white/20 shadow-xl text-white font-sans">
                    Passée
                  </div>
                </div>
                <div className="space-y-4 px-2">
                  <div className="flex items-center gap-4 text-[10px] font-bold uppercase tracking-[0.3em] opacity-30 text-brand-primary font-sans">
                    <span>{format(new Date(outing.date), 'dd MMMM yyyy', { locale: fr })}</span>
                    <span>•</span>
                    <span>{outing.duration}</span>
                    <span>•</span>
                    <span className="flex items-center gap-1"><MapPin size={10} /> {outing.location}</span>
                  </div>
                  <h3 className="text-3xl font-serif font-bold leading-tight group-hover:text-brand-accent transition-colors duration-500 text-brand-primary">
                    {outing.title}
                  </h3>
                  {pricingDisplay.homePrimaryLabel ? (
                    <p className="text-[12px] md:text-[13px] font-semibold text-brand-primary/75">
                      {pricingDisplay.homePrimaryLabel}
                    </p>
                  ) : null}
                  {pricingDisplay.homeBadges.length > 0 ? (
                    <div className="flex flex-wrap gap-2 pt-1">
                      {pricingDisplay.homeBadges.map((badge) => (
                        <span key={badge} className="rounded-full border border-brand-primary/10 bg-brand-paper px-4 py-2 text-[10px] font-bold uppercase tracking-[0.12em] text-brand-primary/70 font-sans">
                          {badge}
                        </span>
                      ))}
                    </div>
                  ) : null}
                </div>
              </motion.div>
                );
              })()
            ))}
          </div>
        </section>
      )}

      {/* Past Outings Toggle - Loading / Empty / Error States */}
      {!pastOutingsLoaded && (
        <section className="space-y-8 px-6 text-center">
          <button
            onClick={handleLoadPastOutings}
            disabled={pastOutingsLoading}
            className="mx-auto bg-brand-primary/5 hover:bg-brand-primary/10 disabled:opacity-60 border border-brand-primary/10 text-brand-primary px-8 md:px-12 py-4 md:py-6 rounded-full text-[9px] md:text-[10px] font-bold uppercase tracking-[0.2em] md:tracking-[0.3em] transition-all duration-500 font-sans"
          >
            {pastOutingsLoading ? 'Chargement...' : '↓ Voir les expéditions passées ↓'}
          </button>
        </section>
      )}

      {pastOutingsLoaded && pastOutingsError && (
        <section className="space-y-8 px-6 text-center">
          <div className="mx-auto rounded-[48px] border border-red-200/50 bg-red-50/50 px-6 py-4 text-red-700/70 text-sm">
            {pastOutingsError}
          </div>
        </section>
      )}

      {pastOutingsLoaded && pastOutings.length === 0 && (
        <section className="space-y-8 px-6 text-center">
          <div className="mx-auto rounded-[48px] border border-dashed border-brand-primary/10 bg-white px-6 py-16 text-brand-primary/40 font-serif italic text-lg">
            Aucune expédition archivée pour le moment.
          </div>
        </section>
      )}

      {!shouldHideNewsletterSignup ? (
      <section className="bg-brand-primary rounded-[80px] p-12 md:p-32 text-center space-y-12 relative overflow-hidden mx-6 shadow-2xl shadow-brand-primary/20">
        <div className="max-w-3xl mx-auto space-y-8 relative z-10">
          <h4 className="text-[10px] font-bold uppercase tracking-[0.5em] text-brand-accent font-sans">Newsletter</h4>
          <h2 className="text-5xl md:text-7xl font-serif font-bold italic text-white leading-tight">
            Ne manquez aucune <br />immersion sauvage.
          </h2>
          <p className="text-white/40 font-serif italic text-xl">
            {isAuthenticated
              ? `Recevoir les nouvelles sorties a l adresse ${newsletterAccountEmail}.`
              : 'Inscrivez-vous pour recevoir les prochaines dates de sorties et des conseils naturalistes.'}
          </p>
          <form className="flex flex-col gap-5 pt-8 font-sans" onSubmit={handleNewsletterSubmit} noValidate>
            {!isAuthenticated ? (
              <>
                <label htmlFor="home-newsletter-email" className="text-left text-sm font-semibold text-white/80">
                  Adresse email
                </label>
                <input
                  id="home-newsletter-email"
                  type="email"
                  placeholder="Votre adresse email"
                  value={newsletterEmail}
                  onChange={(event) => setNewsletterEmail(event.target.value)}
                  autoComplete="email"
                  inputMode="email"
                  aria-describedby="home-newsletter-status"
                  className="flex-1 bg-white/5 border border-white/10 rounded-full px-10 py-6 text-white font-serif italic focus:outline-none focus:border-brand-accent transition-all placeholder:text-white/20"
                />
              </>
            ) : null}
            <label className="flex items-start gap-3 rounded-[24px] border border-white/10 bg-white/5 px-5 py-4 text-left text-sm text-white/75">
              <input
                type="checkbox"
                checked={newsletterConsentChecked}
                onChange={(event) => setNewsletterConsentChecked(event.target.checked)}
                className="mt-1 h-4 w-4 rounded border-white/30 bg-transparent"
              />
              <span>
                J'accepte de recevoir la newsletter et les emails marketing Jacana Nature. Ce choix est optionnel et distinct des CGV.
              </span>
            </label>
            <div className="flex flex-col md:flex-row gap-6">
              <button
                type="submit"
                disabled={newsletterSubmitting}
                className="bg-brand-accent text-white px-12 py-6 rounded-full text-[10px] font-bold uppercase tracking-[0.3em] hover:bg-white hover:text-brand-primary transition-all duration-500 disabled:opacity-60"
              >
                {newsletterSubmitting ? 'Enregistrement...' : (isAuthenticated ? 'Recevoir les nouvelles sorties' : "S'abonner")}
              </button>
            </div>
          </form>
          {newsletterMessage ? (
            <div
              id="home-newsletter-status"
              aria-live="polite"
              className={`rounded-[24px] border px-6 py-4 text-left text-sm ${newsletterMessage.includes('Impossible') || newsletterMessage.includes('Veuillez') || newsletterMessage.includes('devez') || newsletterMessage.includes('Trop de tentatives') || newsletterMessage.includes('serveur') ? 'border-red-200 bg-red-50/90 text-red-700' : 'border-emerald-200 bg-emerald-50/90 text-emerald-800'}`}
            >
              {newsletterMessage}
            </div>
          ) : null}
          <div className="rounded-[24px] border border-white/10 bg-white/5 px-6 py-5 text-left">
            <p className="text-[11px] md:text-xs font-bold uppercase tracking-[0.14em] text-white/70 mb-3">
              Consentement marketing distinct
            </p>
            <p className="text-white/60 text-sm leading-relaxed mb-4">
              L'abonnement newsletter reste optionnel, séparé des CGV et accompagné d'un accès clair à la politique de confidentialité.
            </p>
            <div className="[&_*]:!text-white/80 [&_a]:!underline [&_a]:!underline-offset-4">
              <PrivacyLinksBlock compact />
            </div>
          </div>
        </div>
      </section>
      ) : null}
    </motion.div>
  );
}
